import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { PUT, loading, alert } from "../../components/CustomComponent";
import NicknameCell from "./NicknameCell";
import ColumnPicker, { useHiddenColumns } from "./ColumnPicker";
import useStickyColumns from "./useStickyColumns";
import { DEFAULT_POINTS, INACTIVE_STATUSES, resolveEnrollmentStatus } from "./AttendanceTab";

const API = "v1/cmru/class-sections";

// เครื่องที่ตั้ง "ลดการเคลื่อนไหว": ปิดแอนิเมชัน modal ไปเลย (เหตุผลเดียวกับ AttendanceTab)
const modalAnimation = !(typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches);

// ประเภทคอลัมน์คะแนน: งานเก็บรวมกันเป็น Sum งานเก็บ ส่วนสอบกลางภาค/ปลายภาคแยกแสดง ทั้งหมดรวมเป็น Sum รวม
const COLUMN_KINDS = {
  work: { label: "งานเก็บ" },
  midterm: { label: "สอบกลางภาค" },
  final: { label: "สอบปลายภาค" },
};

// ชุดคอลัมน์ตัวอย่างตามใบคะแนนเดิมของผู้สอน: งานเก็บ 60 + กลางภาค 20 + ปลายภาค 20 = 100
const TEMPLATE_COLUMNS = [
    ["work", "ใบงาน 1", 5],
    ["work", "ใบงาน 2", 5],
    ["work", "ใบงาน 3", 5],
    ["work", "ใบงาน 4", 10],
    ["work", "ใบงาน 5", 10],
    ["work", "ใบงาน 6", 10],
    ["work", "ใบงาน 7", 10],
    ["work", "จิตพิสัย", 5],
    ["midterm", "midterm", 20],
    ["final", "final", 20],
].map(([kind, name, max_score]) => ({ kind, name, max_score }));

// เกณฑ์ตัดเกรดเริ่มต้นของระบบ แก้ได้ต่อกลุ่มเรียนทั้งช่วงคะแนนและตัวอักษร
export const DEFAULT_GRADE_SCALE = {
  ranges: [[0, 49, "F"], [50, 54, "D"], [55, 59, "D+"], [60, 64, "C"], [65, 69, "C+"], [70, 74, "B"], [75, 79, "B+"], [80, 100, "A"]].map(([min, max, grade]) => ({ min, max, grade })),
  missing_grade: "M",
  no_exam_grade: "L",
  withdrawn_grade: "W",
};
const GRADE_KIND_LABEL = { missing: "ไม่ได้ส่งงาน / ไม่ได้สอบ", no_exam: "หมดสิทธิ์สอบ", withdrawn: "ถอนรายวิชา", resigned: "ลาออก" };

const getStudentName = (student) => [student?.prefix_name, [student?.first_name, student?.last_name].filter(Boolean).join(" ")].filter(Boolean).join("") || "-";

// บวกคะแนนแบบจำนวนเต็มของ 1/100 กันทศนิยมเพี้ยน (0.1 + 0.2)
const sum = (values) => values.reduce((acc, value) => acc + Math.round((Number(value) || 0) * 100), 0) / 100;
const formatScore = (value) => (Number.isInteger(value) ? String(value) : String(Math.round(value * 100) / 100));
const isBlank = (value) => value === undefined || value === null || String(value).trim() === "";

// คะแนนเต็มรวมควรเป็น 100 พอดี ขาด/เกินคืนข้อความเตือน (ไม่ห้ามบันทึก แค่เตือนให้รู้)
const FULL_TOTAL = 100;
const totalWarning = (total) => {
  const diff = Math.round((total - FULL_TOTAL) * 100) / 100;
  if (diff === 0) return "";
  return diff < 0 ? `คะแนนเต็มรวม ${formatScore(total)} ยังขาดอีก ${formatScore(-diff)} ถึงจะครบ ${FULL_TOTAL}` : `คะแนนเต็มรวม ${formatScore(total)} เกิน ${FULL_TOTAL} อยู่ ${formatScore(diff)}`;
};

// เกรดจากคะแนนรวม: ใช้ช่วงที่ min สูงสุดซึ่ง <= คะแนน (คะแนนที่ตกช่องว่างระหว่างช่วง เช่น 49.5 จึงได้เกรดของช่วงล่าง) ต่ำกว่าทุกช่วง = เกรดของช่วงต่ำสุด
export const gradeFromTotal = (total, ranges) => {
  const sorted = [...ranges].sort((a, b) => b.min - a.min);
  return (sorted.find((range) => total >= range.min) || sorted[sorted.length - 1])?.grade || "-";
};

// ข้อความกรอกใช้ได้ไหม: ว่าง = ยังไม่กรอก (ใช้ได้) ไม่งั้นต้องเป็นตัวเลข 0..เต็ม
const scoreProblem = (text, max) => {
  if (isBlank(text)) return "";
  const value = Number(text);
  if (!Number.isFinite(value) || value < 0) return "ต้องเป็นตัวเลขไม่ติดลบ";
  if (value > max) return `เกินคะแนนเต็ม ${formatScore(max)}`;
  return "";
};

/**
 * ผลรวมและเกรดของนักศึกษา 1 คน
 * - ถอนรายวิชา / ลาออก -> W · หมดสิทธิ์สอบ (จากแท็บเช็คชื่อ) -> L
 * - M (ไม่ได้ส่งงาน/ไม่ได้สอบ) = มีช่องสอบกลางภาค/ปลายภาคที่ยังไม่ได้กรอก · ช่องงานเก็บที่ว่างนับเป็น 0 (ไม่ทำให้ติด M)
 * - นอกนั้นตัดเกรดจากคะแนนรวมตามเกณฑ์ของกลุ่มเรียน
 */
const computeRow = (scores, columns, scale, status) => {
  const valueOf = (column) => Number(scores[column.uid]) || 0;
  const sumKind = (kind) => sum(columns.filter((column) => column.kind === kind).map(valueOf));
  const total = sum(columns.map(valueOf));
  const examMissing = columns.some((column) => column.kind !== "work" && isBlank(scores[column.uid]));

  let grade;
  let gradeKind = "normal";
  if (INACTIVE_STATUSES.includes(status.key)) [grade, gradeKind] = [scale.withdrawn_grade, status.key];
  else if (status.key === "no_exam") [grade, gradeKind] = [scale.no_exam_grade, "no_exam"];
  else if (examMissing) [grade, gradeKind] = [scale.missing_grade, "missing"];
  else grade = gradeFromTotal(total, scale.ranges);

  return { work: sumKind("work"), midterm: sumKind("midterm"), final: sumKind("final"), total, grade, gradeKind };
};

/**
 * ชื่อคอลัมน์ในหัวตาราง: ยาวเกินความกว้างคอลัมน์ตัดเป็น ... แล้วขึ้นไอคอน i ชี้เมาส์ (หรือแตะบนมือถือ) เพื่อดูชื่อเต็ม
 * วัดจากความกว้างจริง ไม่ใช่นับตัวอักษร เพราะตัวอักษรไทย/อังกฤษกว้างไม่เท่ากัน
 */
const ColumnHead = ({ name, hint }) => {
  const nameRef = useRef(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    const element = nameRef.current;
    if (!element) return undefined;
    const check = () => setTruncated(element.scrollWidth > element.clientWidth + 1);
    check();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(check);
    observer.observe(element);
    return () => observer.disconnect();
  }, [name]);

  const full = hint ? `${name} · ${hint}` : name;
  return (
    <span className="cmru-sc-head" title={full}>
      <span ref={nameRef} className="cmru-sc-name">{name}</span>
      {truncated ? (
        <button type="button" className="cmru-sc-info" title={full} aria-label={`ชื่อเต็ม: ${name}`} onClick={() => alert("", "info", "ชื่อคอลัมน์", full)}>
          <i className="fa-solid fa-circle-info" />
        </button>
      ) : null}
    </span>
  );
};

const ModalHeader = ({ eyebrow, title, subtitle }) => (
  <Modal.Header closeButton>
    <div>
      <p className="stg-eyebrow">{eyebrow}</p>
      <Modal.Title>{title}</Modal.Title>
      {subtitle ? <p className="stg-modal-sub">{subtitle}</p> : null}
    </div>
  </Modal.Header>
);

/* ------------------------------ modal: คอลัมน์คะแนน ------------------------------ */

// คอลัมน์สอบมีคงที่ 2 คอลัมน์ท้ายตารางเสมอ (แก้ชื่อ/คะแนนเต็มได้ ลบไม่ได้) ที่เหลือคืองานเก็บ เพิ่ม/ลบ/ลากเรียงได้
const EXAM_DEFAULTS = { midterm: { name: "midterm", max_score: "20" }, final: { name: "final", max_score: "20" } };
const EXAM_LABEL = { midterm: "สอบกลางภาค", final: "สอบปลายภาค" };
const emptyDrag = { key: "", overKey: "", position: "" };

const ColumnsModal = ({ show, onHide, columns, students, onSave }) => {
  const [rows, setRows] = useState([]);
  const [exams, setExams] = useState(EXAM_DEFAULTS);

  useEffect(() => {
    if (!show) return;
    setRows(columns.filter((column) => column.kind === "work").map((column) => ({ key: column.uid, uid: column.uid, name: column.name, max_score: String(column.max_score) })));
    setExams(
      Object.fromEntries(
        Object.keys(EXAM_DEFAULTS).map((kind) => {
          const column = columns.find((item) => item.kind === kind);
          return [kind, column ? { uid: column.uid, name: column.name, max_score: String(column.max_score) } : { uid: "", ...EXAM_DEFAULTS[kind] }];
        })
      )
    );
  }, [show, columns]);

  const update = (key, patch) => setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const updateExam = (kind, patch) => setExams((current) => ({ ...current, [kind]: { ...current[kind], ...patch } }));
  const add = () => setRows((current) => [...current, { key: `new-${Date.now()}-${current.length}`, uid: "", name: "", max_score: "" }]);

  // ลบคอลัมน์เดิมที่มีคะแนนอยู่แล้ว = คะแนนในคอลัมน์นั้นหายทั้งห้อง ถามก่อน
  const remove = async (row) => {
    const filled = row.uid ? students.filter((enrollment) => !isBlank(enrollment.scores?.[row.uid])).length : 0;
    if (filled && !(await alert("", "warning", `ลบคอลัมน์ "${row.name}"`, `มีคะแนนของ ${filled} คนในคอลัมน์นี้ จะถูกลบไปด้วยเมื่อกดบันทึก ?`, "ลบคอลัมน์", "ยกเลิก"))) return;
    setRows((current) => current.filter((item) => item.key !== row.key));
  };

  const applyTemplate = async () => {
    if (rows.length && !(await alert("", "warning", "ใช้ชุดตัวอย่าง", "แทนที่คอลัมน์งานเก็บทั้งหมดด้วยชุดตัวอย่าง ? คอลัมน์เดิม (และคะแนนในนั้น) จะถูกลบเมื่อกดบันทึก", "แทนที่", "ยกเลิก"))) return;
    setRows(TEMPLATE_COLUMNS.filter((column) => column.kind === "work").map((column, index) => ({ key: `tpl-${index}`, uid: "", name: column.name, max_score: String(column.max_score) })));
    setExams((current) => Object.fromEntries(Object.keys(EXAM_DEFAULTS).map((kind) => [kind, { ...current[kind], ...EXAM_DEFAULTS[kind] }])));
  };

  // ลากเรียงด้วย Pointer Events (ใช้กับจอสัมผัสได้) ลากจากปุ่มจับหน้าแถวเท่านั้น
  const [drag, setDragState] = useState(emptyDrag);
  const dragRef = useRef(emptyDrag);
  const setDrag = (value) => {
    dragRef.current = value;
    setDragState(value);
  };
  const handleGripDown = (event, key) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setDrag({ key, overKey: "", position: "" });
  };
  const handleGripMove = (event) => {
    const current = dragRef.current;
    if (!current.key) return;
    const row = document.elementFromPoint(event.clientX, event.clientY)?.closest?.("[data-col-key]");
    const overKey = row?.getAttribute("data-col-key") || "";
    if (!overKey || overKey === current.key) {
      if (current.overKey) setDrag({ ...current, overKey: "", position: "" });
      return;
    }
    const rect = row.getBoundingClientRect();
    const position = event.clientY - rect.top < rect.height / 2 ? "before" : "after";
    if (current.overKey !== overKey || current.position !== position) setDrag({ ...current, overKey, position });
  };
  const handleGripUp = () => {
    const { key, overKey, position } = dragRef.current;
    setDrag(emptyDrag);
    if (!key || !overKey) return;
    setRows((current) => {
      const moving = current.find((row) => row.key === key);
      const list = current.filter((row) => row.key !== key);
      const targetIndex = list.findIndex((row) => row.key === overKey);
      if (!moving || targetIndex < 0) return current;
      list.splice(position === "after" ? targetIndex + 1 : targetIndex, 0, moving);
      return list;
    });
  };

  const handleSave = async () => {
    for (const [index, row] of rows.entries()) {
      if (!row.name.trim()) return alert("", "warning", "แจ้งเตือน", `กรุณากรอกชื่อคอลัมน์งานเก็บที่ ${index + 1}`);
      const max = Number(row.max_score);
      if (isBlank(row.max_score) || !Number.isFinite(max) || max <= 0) return alert("", "warning", "แจ้งเตือน", `กรุณากรอกคะแนนเต็มของ "${row.name}" เป็นตัวเลขมากกว่า 0`);
    }
    for (const [kind, exam] of Object.entries(exams)) {
      if (!exam.name.trim()) return alert("", "warning", "แจ้งเตือน", `กรุณากรอกชื่อคอลัมน์${EXAM_LABEL[kind]}`);
      const max = Number(exam.max_score);
      if (isBlank(exam.max_score) || !Number.isFinite(max) || max <= 0) return alert("", "warning", "แจ้งเตือน", `กรุณากรอกคะแนนเต็มของ${EXAM_LABEL[kind]}เป็นตัวเลขมากกว่า 0`);
    }
    const toPayload = (row, kind) => ({ ...(row.uid ? { uid: row.uid } : {}), kind, name: row.name.trim(), max_score: Math.round(Number(row.max_score) * 100) / 100 });
    return onSave([...rows.map((row) => toPayload(row, "work")), toPayload(exams.midterm, "midterm"), toPayload(exams.final, "final")]);
  };

  const workTotal = sum(rows.map((row) => row.max_score));
  const examTotal = sum(Object.values(exams).map((exam) => exam.max_score));
  const grandTotal = sum([workTotal, examTotal]);
  const warning = totalWarning(grandTotal);

  return (
    <Modal animation={modalAnimation} show={show} onHide={onHide} centered dialogClassName="stg-modal cmru-modal">
      <ModalHeader eyebrow="Score columns" title="คอลัมน์คะแนน" subtitle="งานเก็บเพิ่ม/ลบ/ลากเรียงได้ · สอบกลางภาคและปลายภาคอยู่ท้ายตารางเสมอ" />
      <Modal.Body>
        <div className="cmru-col-list">
          <div className="cmru-col-row cmru-col-row--head">
            <span />
            <span />
            <span>ชื่อคอลัมน์</span>
            <span>คะแนนเต็ม</span>
            <span />
          </div>

          <div className="cmru-col-group">งานเก็บ <strong>{formatScore(workTotal)}</strong></div>
          {rows.length > 0 ? (
            rows.map((row, index) => (
              <div
                key={row.key}
                data-col-key={row.key}
                className={["cmru-col-row", drag.key === row.key ? "is-dragging" : "", drag.overKey === row.key ? `is-drop-${drag.position}` : ""].join(" ").trim()}
              >
                <button
                  type="button"
                  className="cmru-grip cmru-col-grip"
                  onPointerDown={(event) => handleGripDown(event, row.key)}
                  onPointerMove={handleGripMove}
                  onPointerUp={handleGripUp}
                  onPointerCancel={() => setDrag(emptyDrag)}
                  title="ลากเพื่อเรียงลำดับ"
                  aria-label={`ลากเพื่อเรียงลำดับ ${row.name || `คอลัมน์ที่ ${index + 1}`}`}
                >
                  <i className="fa-solid fa-grip-vertical" />
                </button>
                <span className="cmru-col-index">{index + 1}</span>
                <input type="text" className="form-control" value={row.name} maxLength={100} placeholder="เช่น ใบงาน1" onChange={(event) => update(row.key, { name: event.target.value })} />
                <input type="number" inputMode="decimal" min="0" step="0.5" className="form-control" value={row.max_score} placeholder="เต็ม" onChange={(event) => update(row.key, { max_score: event.target.value })} />
                <button type="button" className="cmru-icon-btn is-danger" onClick={() => remove(row)} title="ลบคอลัมน์" aria-label="ลบคอลัมน์"><i className="fa-solid fa-trash-can" /></button>
              </div>
            ))
          ) : (
            <div className="cmru-col-empty">ยังไม่มีคอลัมน์งานเก็บ กด "เพิ่มคอลัมน์" หรือ "ใช้ชุดตัวอย่าง"</div>
          )}
          <div className="cmru-col-foot-actions">
            <button type="button" className="btn stg-btn stg-btn--ghost" onClick={add}><i className="fa-solid fa-plus" />เพิ่มคอลัมน์</button>
            <button type="button" className="btn stg-btn stg-btn--ghost" onClick={applyTemplate}><i className="fa-solid fa-wand-magic-sparkles" />ใช้ชุดตัวอย่าง</button>
          </div>

          <div className="cmru-col-group">สอบ <strong>{formatScore(examTotal)}</strong></div>
          {Object.keys(EXAM_DEFAULTS).map((kind) => (
            <div className="cmru-col-row is-fixed" key={kind}>
              <span className="cmru-col-lock" title="คอลัมน์คงที่ อยู่ท้ายตารางเสมอ"><i className="fa-solid fa-lock" /></span>
              <span className="cmru-col-index is-exam" title={EXAM_LABEL[kind]}>{kind === "midterm" ? "กลาง" : "ปลาย"}</span>
              <input type="text" className="form-control" value={exams[kind].name} maxLength={100} placeholder={EXAM_LABEL[kind]} onChange={(event) => updateExam(kind, { name: event.target.value })} aria-label={`ชื่อคอลัมน์${EXAM_LABEL[kind]}`} />
              <input type="number" inputMode="decimal" min="0" step="0.5" className="form-control" value={exams[kind].max_score} placeholder="เต็ม" onChange={(event) => updateExam(kind, { max_score: event.target.value })} aria-label={`คะแนนเต็ม${EXAM_LABEL[kind]}`} />
              <span />
            </div>
          ))}
        </div>

        <div className={`cmru-col-foot ${warning ? "has-warning" : ""}`}>
          {warning ? <span className="cmru-col-warning"><i className="fa-solid fa-triangle-exclamation" />{warning}</span> : null}
          <span className="cmru-col-totals"><span className={`is-total ${warning ? "is-off" : ""}`}>รวม <strong>{formatScore(grandTotal)}</strong></span></span>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="stg-modal-actions">
          <button type="button" className="btn stg-btn stg-btn--ghost" onClick={onHide}>ยกเลิก</button>
          <button type="button" className="btn stg-btn stg-btn--primary" onClick={handleSave}>
            <i className="fa-solid fa-floppy-disk" />บันทึกข้อมูล
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

/* ------------------------------ modal: เกณฑ์ตัดเกรด ------------------------------ */

const toScaleForm = (scale) => ({
  ...scale,
  ranges: scale.ranges.map((range, index) => ({ key: `r-${index}-${Date.now()}`, min: String(range.min), max: String(range.max), grade: range.grade })),
});

const GradeScaleModal = ({ show, onHide, scale, onSave }) => {
  const [form, setForm] = useState(() => toScaleForm(DEFAULT_GRADE_SCALE));

  useEffect(() => {
    if (show) setForm(toScaleForm(scale));
  }, [show, scale]);

  const updateRange = (key, patch) => setForm((current) => ({ ...current, ranges: current.ranges.map((range) => (range.key === key ? { ...range, ...patch } : range)) }));
  const removeRange = (key) => setForm((current) => ({ ...current, ranges: current.ranges.filter((range) => range.key !== key) }));
  // ช่วงใหม่ต่อจากช่วงสูงสุดที่มีอยู่
  const addRange = () =>
    setForm((current) => {
      const top = Math.max(-1, ...current.ranges.map((range) => Number(range.max) || 0));
      return { ...current, ranges: [...current.ranges, { key: `r-new-${Date.now()}`, min: String(top + 1), max: String(top + 1), grade: "" }] };
    });

  const handleSave = () => {
    if (!form.ranges.length) return alert("", "warning", "แจ้งเตือน", "ต้องมีช่วงคะแนนอย่างน้อย 1 ช่วง");
    const ranges = [];
    for (const range of form.ranges) {
      const min = Number(range.min);
      const max = Number(range.max);
      if (isBlank(range.min) || isBlank(range.max) || !Number.isFinite(min) || !Number.isFinite(max) || min < 0 || max < 0) return alert("", "warning", "แจ้งเตือน", `ช่วงคะแนนของเกรด "${range.grade || "?"}" ต้องเป็นตัวเลขไม่ติดลบ`);
      if (min > max) return alert("", "warning", "แจ้งเตือน", `เกรด "${range.grade || "?"}": คะแนนต่ำสุด (${min}) ต้องไม่มากกว่าคะแนนสูงสุด (${max})`);
      if (!range.grade.trim()) return alert("", "warning", "แจ้งเตือน", `กรุณากรอกเกรดของช่วง ${min}-${max}`);
      ranges.push({ min, max, grade: range.grade.trim() });
    }
    ranges.sort((a, b) => a.min - b.min);
    for (let index = 1; index < ranges.length; index += 1) {
      if (ranges[index - 1].max >= ranges[index].min) return alert("", "warning", "แจ้งเตือน", `ช่วงคะแนนของเกรด "${ranges[index - 1].grade}" กับ "${ranges[index].grade}" ซ้อนกัน`);
    }
    for (const [field, label] of [["missing_grade", "ไม่ได้ส่งงาน / ไม่ได้สอบ"], ["no_exam_grade", "หมดสิทธิ์สอบ"], ["withdrawn_grade", "ถอนรายวิชา / ลาออก"]]) {
      if (!String(form[field] || "").trim()) return alert("", "warning", "แจ้งเตือน", `กรุณากรอกเกรดของ "${label}"`);
    }
    return onSave({ ranges, missing_grade: form.missing_grade.trim(), no_exam_grade: form.no_exam_grade.trim(), withdrawn_grade: form.withdrawn_grade.trim() });
  };

  const handleSpecial = (event) => setForm((current) => ({ ...current, [event.target.name]: event.target.value }));

  return (
    <Modal animation={modalAnimation} show={show} onHide={onHide} centered dialogClassName="stg-modal cmru-modal">
      <ModalHeader eyebrow="Grade scale" title="เกณฑ์ตัดเกรด" subtitle="ใช้เฉพาะกลุ่มเรียนนี้ แก้ได้ทั้งช่วงคะแนนและตัวอักษรเกรด" />
      <Modal.Body>
        <div className="stg-section pt-2">
          <div className="cmru-scale-list">
            {/* หัวข้อของแต่ละช่อง วางตรงคอลัมน์เดียวกับช่องกรอกด้านล่าง (ช่องว่างเหนือ "-" กับปุ่มลบ) */}
            <div className="cmru-scale-row cmru-scale-row--head" aria-hidden="true">
              <span>คะแนนเริ่ม</span>
              <span />
              <span>คะแนนสิ้นสุด</span>
              <span>เกรด</span>
              <span />
            </div>
            {form.ranges.map((range) => (
              <div className="cmru-scale-row" key={range.key}>
                <input type="number" inputMode="decimal" min="0" className="form-control" value={range.min} placeholder="ต่ำสุด" onChange={(event) => updateRange(range.key, { min: event.target.value })} aria-label="คะแนนต่ำสุด" />
                <span className="cmru-scale-dash">-</span>
                <input type="number" inputMode="decimal" min="0" className="form-control" value={range.max} placeholder="สูงสุด" onChange={(event) => updateRange(range.key, { max: event.target.value })} aria-label="คะแนนสูงสุด" />
                <input type="text" className="form-control cmru-scale-grade" value={range.grade} maxLength={10} placeholder="A" onChange={(event) => updateRange(range.key, { grade: event.target.value })} aria-label="เกรด" />
                <button type="button" className="cmru-icon-btn is-danger" onClick={() => removeRange(range.key)} title="ลบช่วงนี้" aria-label="ลบช่วงนี้"><i className="fa-solid fa-trash-can" /></button>
              </div>
            ))}
          </div>
          <button type="button" className="btn stg-btn stg-btn--ghost mt-2" onClick={addRange}><i className="fa-solid fa-plus" />เพิ่มช่วงคะแนน</button>
          <div className="stg-help">คะแนนที่ตกช่องว่างระหว่างช่วง (เช่น 49.5) จะได้เกรดของช่วงที่ต่ำกว่า</div>
        </div>

        <div className="stg-section">
          <div className="stg-section-head">
            <span className="stg-section-mark"><i className="fa-solid fa-tag" /></span>
            <div>
              <h4 className="stg-section-title">เกรดพิเศษ</h4>
              <p className="stg-section-desc">ใช้แทนเกรดจากคะแนนในกรณีเหล่านี้</p>
            </div>
          </div>
          <div className="row g-3">
            {[
              ["missing_grade", "คะแนนไม่ครบ", "ข้อมูลคะแนน"],
              ["no_exam_grade", "หมดสิทธิ์สอบ", "ข้อมูลเช็คชื่อ"],
              ["withdrawn_grade", "ถอนรายวิชา / ลาออก", "สถานะจากแท็บเช็คชื่อ"],
            ].map(([field, label, help]) => (
              <div className="col-md-4" key={field}>
                <label className="stg-label text-center">{label}</label>
                <input type="text" name={field} className="form-control cmru-scale-grade" value={form[field]} maxLength={10} onChange={handleSpecial} />
                <div className="stg-help text-center">{help}</div>
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="stg-modal-actions">
          <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => setForm(toScaleForm(DEFAULT_GRADE_SCALE))} title="F 0-49 · D 50-54 · D+ 55-59 · C 60-64 · C+ 65-69 · B 70-74 · B+ 75-79 · A 80-100 · M · L · W">
            <i className="fa-solid fa-rotate-left" />ค่าเริ่มต้น
          </button>
          <button type="button" className="btn stg-btn stg-btn--ghost" onClick={onHide}>ยกเลิก</button>
          <button type="button" className="btn stg-btn stg-btn--primary" onClick={handleSave}>
            <i className="fa-solid fa-floppy-disk" />บันทึกข้อมูล
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

/**
 * แท็บคะแนนของกลุ่มเรียน: ตารางนักศึกษา x คอลัมน์คะแนน (งานเก็บ -> Sum -> กลางภาค -> ปลายภาค -> Sum รวม -> Grade -> Rank)
 * กรอกคะแนนในช่องได้เลย ไม่บันทึกอัตโนมัติ: ค้างอยู่ใน draft จนกว่าจะกด "บันทึก" (แบบเดียวกับแท็บเช็คชื่อ)
 * คอลัมน์คะแนนและเกณฑ์ตัดเกรดตั้งได้ต่อกลุ่มเรียนจากปุ่มมุมขวาบน
 */
export default function ScoreTab({ detail, setDetail, reload, onDirtyChange }) {
  const [isSaving, setIsSaving] = useState(false);
  const [showColumns, setShowColumns] = useState(false);
  const [showScale, setShowScale] = useState(false);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  // แถวที่กดเลือกไว้ (is-focus): เด่นค้างไว้และแถวอื่นจางลง กดที่ส่วนว่างของแถวเดิมซ้ำเพื่อยกเลิก
  const [focusUid, setFocusUid] = useState("");
  const handleRowClick = (event, uid) => {
    if (event.target.closest("button, input, a")) setFocusUid(uid);
    else setFocusUid((current) => (current === uid ? "" : uid));
  };


  const sectionUid = detail.uid;
  const columns = detail.score_columns || [];
  const workColumns = columns.filter((column) => column.kind === "work");
  const examColumns = [...columns.filter((column) => column.kind === "midterm"), ...columns.filter((column) => column.kind === "final")];
  // ลำดับคอลัมน์ที่กรอกได้ (ใช้เลื่อนช่องด้วยคีย์บอร์ด)
  const inputColumns = [...workColumns, ...examColumns];

  // คอลัมน์ที่ผู้ใช้เลือกซ่อน (จำไว้ในเครื่อง) ผลรวม/เกรดยังคิดจากทุกคอลัมน์ ซ่อนแค่การแสดง
  const columnPick = useHiddenColumns("cmru-sc-columns");
  const { show } = columnPick;
  const shownWork = workColumns.filter((column) => show(`col:${column.uid}`));
  const shownExam = examColumns.filter((column) => show(`col:${column.uid}`));
  const showWorkSum = workColumns.length > 0 && show("work_sum");
  const showTotal = show("total");
  // แถวล่างของหัวตาราง (คะแนนเต็ม) มีเฉพาะเมื่อยังมีคอลัมน์คะแนน/ผลรวมแสดงอยู่
  const headRows = shownWork.length + shownExam.length > 0 || showWorkSum || showTotal ? 2 : 1;
  const visibleCount = 2 + ["code", "nick", "grade", "rank"].filter(show).length + shownWork.length + shownExam.length + (showWorkSum ? 1 : 0) + (showTotal ? 1 : 0);
  const columnOptions = [
    { key: "code", label: "รหัส (นศ.)" },
    { key: "nick", label: "ชื่อเล่น" },
    ...workColumns.map((column) => ({ key: `col:${column.uid}`, label: column.name, group: "งานเก็บ" })),
    ...(workColumns.length ? [{ key: "work_sum", label: "Sum งานเก็บ", group: "งานเก็บ" }] : []),
    ...examColumns.map((column) => ({ key: `col:${column.uid}`, label: column.name, group: "สอบ" })),
    { key: "total", label: "Sum รวม", group: "สรุป" },
    { key: "grade", label: "Grade", group: "สรุป" },
    { key: "rank", label: "Rank", group: "สรุป" },
  ];

  // ตำแหน่งเกาะซ้ายของคอลัมน์ เลขที่/รหัส/ชื่อ/ชื่อเล่น วัดจากความกว้างจริง (ดู useStickyColumns)
  // ตารางถูกถอด/ใส่ใหม่ตอนจำนวนคอลัมน์เปลี่ยน (0 -> มี) และหัวคอลัมน์รหัสถูกถอดตอนซ่อน ต้องผูก observer ใหม่
  const { tableRef, seqRef, codeRef, nameRef } = useStickyColumns([detail.score_columns?.length, columnPick.hidden]);
  const workMax = sum(workColumns.map((column) => column.max_score));
  const totalMax = sum(columns.map((column) => column.max_score));
  // เกณฑ์ที่บันทึกไว้ทับค่าเริ่มต้น (กันข้อมูลเก่าที่ไม่มีบาง key แล้วช่องกรอกกลายเป็น uncontrolled)
  const scale = detail.grade_scale?.ranges?.length ? { ...DEFAULT_GRADE_SCALE, ...detail.grade_scale } : DEFAULT_GRADE_SCALE;
  const points = { ...DEFAULT_POINTS, ...(detail.attendance_points || {}) };
  const threshold = detail.no_exam_threshold;

  // คะแนนที่แก้ค้างไว้: key = "enrollmentUid:columnUid" -> ข้อความในช่อง ("" = ล้างช่อง) เก็บเฉพาะช่องที่ต่างจากในระบบ
  const [draft, setDraft] = useState({});
  const dirtyCount = Object.keys(draft).length;
  const isDirty = dirtyCount > 0;
  const discardDraft = () => setDraft({});

  // คอลัมน์ถูกลบ/เปลี่ยนหลังตั้งค่า: ทิ้งของค้างที่อ้างคอลัมน์ที่ไม่มีแล้ว
  const columnKey = columns.map((column) => column.uid).join(",");
  useEffect(() => {
    const known = new Set(columnKey.split(",").filter(Boolean));
    setDraft((current) => Object.fromEntries(Object.entries(current).filter(([key]) => known.has(key.split(":")[1]))));
  }, [columnKey]);

  const savedText = (enrollment, columnUid) => (isBlank(enrollment.scores?.[columnUid]) ? "" : String(enrollment.scores[columnUid]));
  const normalize = (text) => (isBlank(text) ? "" : Number.isFinite(Number(text)) ? String(Number(text)) : String(text).trim());

  const stageScore = (enrollment, columnUid, text) => {
    const key = `${enrollment.uid}:${columnUid}`;
    setDraft((current) => {
      const next = { ...current };
      if (normalize(text) === normalize(savedText(enrollment, columnUid))) delete next[key];
      else next[key] = text;
      return next;
    });
  };

  // รายชื่อที่ใช้แสดงผล = ข้อมูลในระบบ + ของค้าง (ผลรวม/เกรดคิดจากชุดนี้ เห็นผลก่อนกดบันทึก) ช่องที่กรอกผิดนับเป็นว่าง
  const students = (detail.students || []).map((enrollment) => {
    const scores = { ...(enrollment.scores || {}) };
    columns.forEach((column) => {
      const key = `${enrollment.uid}:${column.uid}`;
      if (!(key in draft)) return;
      const value = Number(draft[key]);
      if (isBlank(draft[key]) || !Number.isFinite(value)) delete scores[column.uid];
      else scores[column.uid] = value;
    });
    return { ...enrollment, scores };
  });

  const invalidCount = Object.entries(draft).filter(([key, text]) => {
    const column = columns.find((item) => item.uid === key.split(":")[1]);
    return column && scoreProblem(text, column.max_score);
  }).length;

  const handleSave = async () => {
    if (!isDirty || isSaving) return;
    if (invalidCount) {
      await alert("", "warning", "แจ้งเตือน", `มีช่องที่กรอกไม่ถูกต้อง ${invalidCount} ช่อง (ขอบสีแดง) กรุณาแก้ก่อนบันทึก`);
      return;
    }

    // ช่องที่ลบจนว่างส่ง score = null เพื่อลบคะแนนเดิมในระบบ
    const records = Object.entries(draft).map(([key, text]) => {
      const [enrollment_uid, column_uid] = key.split(":");
      return { enrollment_uid, column_uid, score: isBlank(text) ? null : Math.round(Number(text) * 100) / 100 };
    });

    setIsSaving(true);
    loading(true);
    // keepEmpty: null มีความหมาย (ล้างช่อง) ปกติ PUT จะตัด key ที่เป็น null ทิ้งก่อนส่ง
    const result = await PUT(`${API}/${sectionUid}/scores`, { records }, null, { keepEmpty: true });
    if (result?.status === true) {
      await reload();
      discardDraft();
      alert("", "success", "สำเร็จ", `บันทึกคะแนนแล้ว ${records.length} ช่อง`);
    }
    loading(false);
    setIsSaving(false);
  };

  const handleCancel = async () => {
    if (!isDirty) return;
    if (!(await alert("", "warning", "ยกเลิกการเปลี่ยนแปลง", `ทิ้งคะแนนที่ยังไม่ได้บันทึก ${dirtyCount} ช่อง แล้วกลับไปใช้ข้อมูลล่าสุดในระบบ ?`, "ทิ้งการเปลี่ยนแปลง", "กลับไปแก้ต่อ"))) return;
    discardDraft();
  };

  // แจ้งหน้าหลักว่ามีของค้าง (ใช้ถามยืนยันก่อนสลับแท็บ/กดกลับ)
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onDirtyChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps

  // กันข้อมูลหายตอนมีของค้าง: ปิด/รีเฟรชหน้า (เบราว์เซอร์ถามเอง) และกดลิงก์เมนูไปหน้าอื่น (ถามยืนยันก่อน)
  useEffect(() => {
    if (!isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const handleLinkClick = async (event) => {
      const link = event.target.closest?.("a[href]");
      if (!link || link.target === "_blank" || link.origin !== window.location.origin) return;

      event.preventDefault();
      event.stopPropagation();
      if (await alert("", "warning", "ยังไม่ได้บันทึกข้อมูล", `มีคะแนนที่ยังไม่ได้บันทึก ${dirtyCount} ช่อง ออกจากหน้านี้โดยไม่บันทึก ?`, "ออกโดยไม่บันทึก", "อยู่หน้านี้ต่อ")) {
        discardDraft();
        navigate(link.pathname + link.search);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleLinkClick, true);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleLinkClick, true);
    };
  }, [isDirty, dirtyCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Enter / ลูกศรลง = ไปช่องเดียวกันของคนถัดไป · ลูกศรขึ้น = คนก่อนหน้า (ช่องตัวเลขปกติลูกศรจะบวก/ลบค่า ซึ่งไม่ใช่สิ่งที่ต้องการตอนไล่กรอก)
  const handleInputKeyDown = (event, columnIndex, rowIndex) => {
    const step = event.key === "Enter" || event.key === "ArrowDown" ? 1 : event.key === "ArrowUp" ? -1 : 0;
    if (!step) return;
    event.preventDefault();
    const target = document.querySelector(`[data-score-cell="${columnIndex}:${rowIndex + step}"]`);
    if (target) {
      target.focus();
      target.select?.();
    }
  };

  const saveColumns = async (nextColumns) => {
    loading(true);
    const result = await PUT(`${API}/${sectionUid}/score-columns`, { columns: nextColumns }, null, { keepEmpty: true });
    loading(false);
    if (result?.status === true) {
      // โหลดข้อมูลใหม่ให้เสร็จก่อนค่อยปิด modal (เหตุผลเดียวกับ SettingsModal ของแท็บเช็คชื่อ)
      await reload();
      setShowColumns(false);
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    }
  };

  const saveScale = async (nextScale) => {
    loading(true);
    const result = await PUT(`${API}/${sectionUid}/grade-scale`, nextScale, null, { keepEmpty: true });
    loading(false);
    if (result?.status === true) {
      await reload();
      setShowScale(false);
    } else {
      alert("", "warning", "แจ้งเตือน", result?.message || "เกิดข้อผิดพลาด กรุณาลองใหม่อีกครั้ง");
    }
  };

  const useTemplate = async () => {
    if (!(await alert("", "info", "ใช้ชุดตัวอย่าง", "สร้างคอลัมน์ตามชุดตัวอย่าง: งานเก็บ 8 คอลัมน์ (60) + กลางภาค (20) + ปลายภาค (20) = 100 ? แก้ไขภายหลังได้", "สร้างคอลัมน์", "ยกเลิก"))) return;
    saveColumns(TEMPLATE_COLUMNS);
  };

  // แถวพร้อมผลรวม/เกรด แล้วจัดอันดับจากคะแนนรวม (คนถอนรายวิชาไม่จัดอันดับ คะแนนเท่ากันได้อันดับเดียวกัน)
  const rows = students.map((enrollment) => {
    const status = resolveEnrollmentStatus(enrollment, points, threshold);
    return { enrollment, status, ...computeRow(enrollment.scores, columns, scale, status) };
  });
  const ranked = rows.filter((row) => !INACTIVE_STATUSES.includes(row.status.key));
  rows.forEach((row) => {
    row.rank = INACTIVE_STATUSES.includes(row.status.key) ? null : 1 + ranked.filter((other) => other.total > row.total).length;
  });

  // ค่าเฉลี่ยท้ายตารางคิดจากช่องที่กรอกแล้วของทั้งห้อง (ไม่สนการค้นหา)
  const average = (values) => (values.length ? sum(values) / values.length : null);
  const columnAverage = Object.fromEntries(columns.map((column) => [column.uid, average(students.map((enrollment) => enrollment.scores[column.uid]).filter((value) => !isBlank(value)))]));
  const workAverage = average(ranked.map((row) => row.work));
  const totalAverage = average(ranked.map((row) => row.total));
  const formatAverage = (value) => (value === null ? "" : (Math.round(value * 10) / 10).toString());

  const keyword = search.trim().toLowerCase();
  const visibleRows = keyword
    ? rows.filter(({ enrollment }) => `${enrollment.student?.student_code || ""} ${getStudentName(enrollment.student)} ${enrollment.student?.nickname || ""}`.toLowerCase().includes(keyword))
    : rows;

  const scaleDesc = [...scale.ranges].sort((a, b) => b.min - a.min);
  const panelDesc = `กรอกคะแนนในช่อง · Enter = ไปคนถัดไป · แก้เสร็จแล้วกดบันทึก · คะแนนเต็ม ${formatScore(totalMax)} · เกรดคิดจากคะแนนรวมตามเกณฑ์ของกลุ่มเรียนนี้`;

  const renderScoreInput = (enrollment, column, rowIndex) => {
    const key = `${enrollment.uid}:${column.uid}`;
    const text = key in draft ? draft[key] : savedText(enrollment, column.uid);
    const problem = scoreProblem(text, column.max_score);
    return (
      <input
        type="number"
        inputMode="decimal"
        min="0"
        max={column.max_score}
        step="0.5"
        className={`cmru-sc-input ${isBlank(text) ? "is-empty" : ""} ${key in draft ? "is-dirty" : ""} ${problem ? "is-invalid" : ""}`}
        value={text}
        data-score-cell={`${inputColumns.indexOf(column)}:${rowIndex}`}
        onChange={(event) => stageScore(enrollment, column.uid, event.target.value)}
        onKeyDown={(event) => handleInputKeyDown(event, inputColumns.indexOf(column), rowIndex)}
        // โฟกัสเข้าช่อง (คลิกหรือกด Enter/ลูกศรมาจากแถวอื่น) = แถวนี้เด่นขึ้นและแถวอื่นจางลง ตามเคอร์เซอร์ไปทุกแถว
        onFocus={(event) => {
          event.target.select();
          setFocusUid(enrollment.uid);
        }}
        onWheel={(event) => event.target.blur()}
        title={problem || `${column.name} · เต็ม ${formatScore(column.max_score)}`}
        aria-label={`${getStudentName(enrollment.student)} ${column.name}`}
      />
    );
  };

  return (
    <div className="stg-panel">
      <div className="stg-panel-head">
        <div className="stg-panel-intro">
          <span className="stg-panel-icon"><i className="fa-solid fa-star" /></span>
          <div style={{ minWidth: 0 }}>
            <p className="stg-eyebrow">Scores</p>
            <h3 className="stg-panel-title">คะแนน</h3>
            <p className="stg-panel-desc cmru-one-line" title={panelDesc}>{panelDesc}</p>
          </div>
        </div>
        <div className="stg-panel-actions">
          {columns.length > 0 && <ColumnPicker options={columnOptions} hidden={columnPick.hidden} onToggle={columnPick.toggle} onReset={columnPick.reset} />}
          <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => setShowColumns(true)}>
            <i className="fa-solid fa-table-columns" />คอลัมน์คะแนน
          </button>
          <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => setShowScale(true)}>
            <i className="fa-solid fa-ranking-star" />เกณฑ์เกรด
          </button>
        </div>
      </div>

      <div className="stg-panel-body">
        {/* เกณฑ์เกรดแบบย่อ: กวาดตาดูได้โดยไม่ต้องเปิดตั้งค่า */}
        <div className="cmru-att-legend mb-3">
          {scaleDesc.map((range) => (
            <span key={`${range.grade}-${range.min}`}><span className="cmru-grade is-normal">{range.grade}</span>{formatScore(range.min)}-{formatScore(range.max)}</span>
          ))}
          <span><span className="cmru-grade is-missing">{scale.missing_grade}</span>{GRADE_KIND_LABEL.missing}</span>
          <span><span className="cmru-grade is-no_exam">{scale.no_exam_grade}</span>{GRADE_KIND_LABEL.no_exam}</span>
          <span><span className="cmru-grade is-withdrawn">{scale.withdrawn_grade}</span>{GRADE_KIND_LABEL.withdrawn} / {GRADE_KIND_LABEL.resigned}</span>
        </div>

        {columns.length > 0 && totalWarning(totalMax) ? (
          <div className="cmru-sc-warning mb-3" role="alert">
            <i className="fa-solid fa-triangle-exclamation" />
            <span>{totalWarning(totalMax)} · แก้คะแนนเต็มได้ที่ปุ่ม "คอลัมน์คะแนน"</span>
          </div>
        ) : null}

        {columns.length === 0 ? (
          <div className="stg-empty cmru-sc-empty">
            <i className="fa-solid fa-table-columns" />
            <p>ยังไม่ได้ตั้งคอลัมน์คะแนนของกลุ่มเรียนนี้</p>
            <div className="cmru-sc-empty-actions">
              <button type="button" className="btn stg-btn stg-btn--primary" onClick={useTemplate}><i className="fa-solid fa-wand-magic-sparkles" />ใช้ชุดตัวอย่าง (60 + 20 + 20)</button>
              <button type="button" className="btn stg-btn stg-btn--ghost" onClick={() => setShowColumns(true)}><i className="fa-solid fa-plus" />ตั้งคอลัมน์เอง</button>
            </div>
          </div>
        ) : (
          <>
            <div className="stg-search mb-3">
              <i className="fa-solid fa-magnifying-glass" />
              <input type="text" className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="ค้นหารหัส (นศ.) ชื่อ-สกุล หรือชื่อเล่น..." />
            </div>

            <div className="cmru-att-wrap stg-table-wrap">
              {/* is-no-lock: ซ่อนชื่อเล่นแล้วคอลัมน์ชื่อเป็นตัวสุดท้ายที่เกาะซ้าย (เหมือนแท็บเช็คชื่อ) */}
              <table className={`cmru-att-table cmru-sc-table ${show("nick") ? "" : "is-no-lock"}`} ref={tableRef}>
                <thead>
                  {/* แถวบน = ชื่อคอลัมน์ แถวล่าง = คะแนนเต็ม (ตามใบคะแนนเดิม) */}
                  <tr>
                    <th rowSpan={headRows} className="cmru-att-seq" ref={seqRef}>เลขที่</th>
                    {show("code") && <th rowSpan={headRows} className="cmru-att-code" ref={codeRef}>รหัส (นศ.)</th>}
                    <th rowSpan={headRows} className="cmru-att-name" ref={nameRef}>ชื่อ-สกุล (นศ.)</th>
                    {show("nick") && <th rowSpan={headRows} className="cmru-att-nick cmru-att-lock">ชื่อเล่น</th>}
                    {shownWork.map((column) => <th key={column.uid} className="cmru-sc-col"><ColumnHead name={column.name} hint={`${COLUMN_KINDS.work.label} เต็ม ${formatScore(column.max_score)}`} /></th>)}
                    {showWorkSum && <th className="cmru-sc-col cmru-sc-sum" title="รวมงานเก็บ">Sum</th>}
                    {shownExam.map((column) => <th key={column.uid} className="cmru-sc-col"><ColumnHead name={column.name} hint={`${COLUMN_KINDS[column.kind].label} เต็ม ${formatScore(column.max_score)}`} /></th>)}
                    {showTotal && <th className="cmru-sc-col cmru-sc-sum cmru-sc-total" title="รวมทั้งหมด">Sum</th>}
                    {show("grade") && <th rowSpan={headRows} className="cmru-sc-col cmru-sc-grade-head">Grade</th>}
                    {show("rank") && <th rowSpan={headRows} className="cmru-sc-col">Rank</th>}
                  </tr>
                  {headRows === 2 && (
                    <tr>
                      {shownWork.map((column) => <th key={column.uid} className="cmru-sc-col cmru-sc-max">{formatScore(column.max_score)}</th>)}
                      {showWorkSum && <th className="cmru-sc-col cmru-sc-sum cmru-sc-max">{formatScore(workMax)}</th>}
                      {shownExam.map((column) => <th key={column.uid} className="cmru-sc-col cmru-sc-max">{formatScore(column.max_score)}</th>)}
                      {showTotal && <th className="cmru-sc-col cmru-sc-sum cmru-sc-total cmru-sc-max">{formatScore(totalMax)}</th>}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {visibleRows.length > 0 ? (
                    visibleRows.map((row, rowIndex) => {
                      const { enrollment, status } = row;
                      return (
                        <tr
                          key={enrollment.uid}
                          className={[status.key !== "normal" ? `is-${status.key}` : "", focusUid === enrollment.uid ? "is-focus" : ""].join(" ").trim() || undefined}
                          onClick={(event) => handleRowClick(event, enrollment.uid)}
                        >
                          <td className="cmru-att-seq"><span className="cmru-seq">{enrollment.seq_no ?? "-"}</span></td>
                          {show("code") && <td className="cmru-att-code">{enrollment.student?.student_code || "-"}</td>}
                          <td className="cmru-att-name">{getStudentName(enrollment.student)}</td>
                          {/* ชื่อเล่นบันทึกทันที ไม่รวมกับคะแนนที่รอบันทึก */}
                          {show("nick") && (
                            <td className="cmru-att-nick cmru-att-lock cmru-nick-cell">
                              <NicknameCell
                                sectionUid={sectionUid}
                                enrollment={enrollment}
                                rowIndex={rowIndex}
                                onSaved={(nickname) => setDetail((current) => ({
                                  ...current,
                                  students: current.students.map((item) => (item.uid === enrollment.uid ? { ...item, student: { ...item.student, nickname } } : item)),
                                }))}
                              />
                            </td>
                          )}
                          {shownWork.map((column) => <td key={column.uid} className="cmru-sc-col">{renderScoreInput(enrollment, column, rowIndex)}</td>)}
                          {showWorkSum && <td className="cmru-sc-col cmru-sc-sum">{formatScore(row.work)}</td>}
                          {shownExam.map((column) => <td key={column.uid} className="cmru-sc-col">{renderScoreInput(enrollment, column, rowIndex)}</td>)}
                          {showTotal && <td className="cmru-sc-col cmru-sc-sum cmru-sc-total">{formatScore(row.total)}</td>}
                          {show("grade") && (
                            <td className="cmru-sc-col" title={GRADE_KIND_LABEL[row.gradeKind] || `คะแนนรวม ${formatScore(row.total)} / ${formatScore(totalMax)}`}>
                              <span className={`cmru-grade is-${row.gradeKind}`}>{row.grade}</span>
                            </td>
                          )}
                          {show("rank") && <td className="cmru-sc-col cmru-sc-rank">{row.rank ?? "-"}</td>}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={visibleCount} className="stg-empty">
                        <i className="fa-regular fa-folder-open" />{rows.length ? "ไม่พบนักศึกษาที่ค้นหา" : "ยังไม่มีรายชื่อในกลุ่มเรียนนี้"}
                      </td>
                    </tr>
                  )}
                </tbody>
                {/* แถวสรุปเกาะล่างตาราง: ค่าเฉลี่ยของแต่ละคอลัมน์ เห็นทันทีว่างานไหนคะแนนต่ำทั้งห้อง */}
                {rows.length > 0 && (
                  <tfoot>
                    <tr>
                      <td className="cmru-att-seq" />
                      {show("code") && <td className="cmru-att-code" />}
                      <td className="cmru-att-name">{show("nick") ? "" : "เฉลี่ย"}</td>
                      {show("nick") && <td className="cmru-att-nick cmru-att-lock">เฉลี่ย</td>}
                      {shownWork.map((column) => <td key={column.uid} className="cmru-sc-col">{formatAverage(columnAverage[column.uid])}</td>)}
                      {showWorkSum && <td className="cmru-sc-col cmru-sc-sum">{formatAverage(workAverage)}</td>}
                      {shownExam.map((column) => <td key={column.uid} className="cmru-sc-col">{formatAverage(columnAverage[column.uid])}</td>)}
                      {showTotal && <td className="cmru-sc-col cmru-sc-sum cmru-sc-total">{formatAverage(totalAverage)}</td>}
                      {show("grade") && <td />}
                      {show("rank") && <td />}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </div>

      {isDirty && (
        <div className="cmru-unsaved" role="status" aria-live="polite">
          <div className="cmru-unsaved-text">
            <i className="fa-solid fa-triangle-exclamation" />
            <div>
              <strong>ยังไม่ได้บันทึกข้อมูล</strong>
              <span>
                เปลี่ยนแปลงคะแนน {dirtyCount} ช่อง{invalidCount ? ` · กรอกไม่ถูกต้อง ${invalidCount} ช่อง` : ""} · กดบันทึกเพื่อเก็บลงระบบ
              </span>
            </div>
          </div>
          <div className="cmru-unsaved-actions">
            <button type="button" className="btn stg-btn stg-btn--ghost" onClick={handleCancel} disabled={isSaving}>ยกเลิก</button>
            <button type="button" className="btn stg-btn stg-btn--primary" onClick={handleSave} disabled={isSaving}>
              <i className="fa-solid fa-floppy-disk" />บันทึก
            </button>
          </div>
        </div>
      )}

      <ColumnsModal show={showColumns} onHide={() => setShowColumns(false)} columns={columns} students={detail.students || []} onSave={saveColumns} />
      <GradeScaleModal show={showScale} onHide={() => setShowScale(false)} scale={scale} onSave={saveScale} />
    </div>
  );
}
